/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { withEnterKey } from '../../internal/with-enter-key.js';
import * as Assets from '../../assets.js';
/**
 * WebComponent.
 */
let PwcUserInput = class PwcUserInput extends LitElement {
    constructor() {
        super(...arguments);
        this.value = '';
        this.placeholder = '';
        this.disableSendPrompt = false;
        this._isMultiline = false;
    }
    /**
     * @inheritdoc
     */
    firstUpdated() {
        // init the resize observer.
        this._resizeObserver = new ResizeObserver(() => {
            this._adjustHeight();
        });
        // observe the screen size.
        this._resizeObserver.observe(this._visibleTextarea);
    }
    /**
     * @inheritdoc
     */
    disconnectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
        // unregister resize observer.
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = undefined;
        }
    }
    /**
     * Render the output.
     */
    render() {
        return html `
      <div class="content-wrapper is-hidden">
        <div class="content is-not-multiline">
          <textarea
            .value=${this.value}
            placeholder="Hidden Textarea"
            rows="1"
          ></textarea>
          <div class="send-button">${Assets.iconSend()}</div>
        </div>
      </div>
      <div class="content-wrapper is-visible">
        <div
          class=${classMap({
            content: true,
            'is-multiline': this._isMultiline,
            'is-not-multiline': !this._isMultiline,
        })}
        >
          <textarea
            .value=${this.value}
            @input=${(e) => this._handleInput(e)}
            placeholder=${this.placeholder}
            rows="1"
            @keydown=${(event) => this._handleKeyDown(event)}
          ></textarea>
          <div
            class=${classMap({
            'send-button': true,
            'is-enabled': !this.disableSendPrompt,
            'is-disabled': this.disableSendPrompt,
        })}
            tabindex="0"
            @click=${() => this._emitPostMessage()}
            @keydown=${(event) => withEnterKey(event, () => this._emitPostMessage())}
          >
            ${Assets.iconSend()}
          </div>
        </div>
      </div>
    `;
    }
    /**
     * Handle the key down event.
     */
    _handleKeyDown(event) {
        // send the message on enter key.
        if (event.key === 'Enter') {
            if (event.shiftKey) {
                // scroll to the bottom when adding a new line.
                window.setTimeout(() => {
                    this._visibleTextarea.scrollTop = this._visibleTextarea.scrollHeight;
                }, 0);
            }
            else {
                // this is a simple enter keypress, send the prompt.
                this._emitPostMessage();
                event.preventDefault();
            }
        }
    }
    /**
     * Emit an event representing the post message.
     */
    _emitPostMessage() {
        // guard.
        if (!this._visibleTextarea) {
            return;
        }
        // get message.
        let message = this._visibleTextarea.value;
        // cleanup message.
        message = message.trim();
        // do not process empty messages.
        if (message.length === 0) {
            return;
        }
        // create custom event.
        const event = new CustomEvent('pwc-user-input-post-message', {
            detail: {
                prompt: message,
            },
        });
        // dispatch it.
        this.dispatchEvent(event);
    }
    _handleInput(e) {
        const target = e.target;
        this.value = target.value;
        this._adjustHeight();
    }
    /**
     * Adjust height of input.
     */
    _adjustHeight() {
        // guard.
        if (!this._visibleTextarea || !this._hiddenTextarea) {
            return;
        }
        // reset height to auto to get the correct scrollHeight.
        this._hiddenTextarea.style.height = 'auto';
        // calculate new height based on scrollHeight, but limit to 4 rows.
        const lineHeight = parseFloat(getComputedStyle(this._hiddenTextarea).lineHeight);
        const maxHeight = lineHeight * 4 + 10; // 10px padding.
        const newHeight = Math.min(this._hiddenTextarea.scrollHeight, maxHeight);
        // update height.
        this._hiddenTextarea.style.height = `${newHeight}px`;
        this._visibleTextarea.style.height = `${newHeight}px`;
        // add scrollbar if content exceeds max height.
        if (this._hiddenTextarea.scrollHeight > maxHeight) {
            this._hiddenTextarea.style.overflowY = 'auto';
            this._visibleTextarea.style.overflowY = 'auto';
        }
        else {
            this._hiddenTextarea.style.overflowY = 'hidden';
            this._visibleTextarea.style.overflowY = 'hidden';
        }
        // height of one row.
        const oneRowHeight = lineHeight + 10; // 10px padding.
        if (newHeight > oneRowHeight) {
            this._isMultiline = true;
        }
        else {
            this._isMultiline = false;
        }
    }
    /**
     * @inheritdoc
     */
    updated(changedProperties) {
        super.updated(changedProperties);
        this._adjustHeight();
    }
};
PwcUserInput.styles = css `
    :host {
      position: relative;
      display: flex;
      flex: auto;
      flex-direction: column;
    }

    /* Content Wrapper */
    .content-wrapper {
      position: relative;
      display: flex;
      flex: auto;
      padding: 3px;
      border-radius: 7px;
      background: linear-gradient(355deg, #48b4d8, #2a2ed1, #ff7e3b);
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }

    .content {
      background-color: #fff;
      border-radius: 4px;
      display: flex;
      flex: auto;
      align-items: center;
      padding: 7px;
    }

    .content-wrapper.is-hidden {
      position: absolute;
      visibility: hidden;
    }

    .content.is-multiline {
      flex-direction: row;
      align-items: center;
    }

    .content.is-not-multiline {
      flex-direction: row;
    }

    /* Textarea */
    textarea {
      flex: auto;
      width: 100%;
      box-sizing: border-box;
      resize: none;
      overflow: hidden;
      min-height: 1.5rem;
      margin-right: 4px;
      line-height: 1.5em;
      border: none;
      border-radius: 4px;
      font-family: inherit;
      font-size: inherit;
      outline: none;
      padding: 5px;
      white-space: break-spaces;
      transition: height 100ms ease;
    }

    textarea.is-hidden {
      visibility: hidden;
    }

    /* Send Button */
    .send-button,
    .send-button .icon-send {
      height: 35px;
      width: 35px;
    }

    .send-button.is-enabled {
      opacity: 0.9;
      cursor: pointer;
    }

    .send-button.is-disabled {
      opacity: 0.5;
      cursor: inherit;
    }
  `;
__decorate([
    property({ type: String })
], PwcUserInput.prototype, "value", void 0);
__decorate([
    property({ type: String })
], PwcUserInput.prototype, "placeholder", void 0);
__decorate([
    property({ attribute: 'disable-send-prompt', type: Boolean })
], PwcUserInput.prototype, "disableSendPrompt", void 0);
__decorate([
    state()
], PwcUserInput.prototype, "_isMultiline", void 0);
__decorate([
    query('.is-visible textarea')
], PwcUserInput.prototype, "_visibleTextarea", void 0);
__decorate([
    query('.is-hidden textarea')
], PwcUserInput.prototype, "_hiddenTextarea", void 0);
PwcUserInput = __decorate([
    customElement('pwc-user-input')
], PwcUserInput);
export { PwcUserInput };
//# sourceMappingURL=pwc-user-input.js.map